import { ApplicationStatus } from 'legacy/shared-types/enums/application-status.enum';
import { CaseSidebarItemId, CaseSidebarSection, StatusSidebarChange } from '../types';

/**
 * OO happy path: APPLICATION_SUBMITTED first, then DIP_ACCEPTED (unlike BTL).
 *
 * APPLICATION_SUBMITTED (and mapped pre-DIP statuses)
 *   DOCUMENTS: Customer files, Evidence
 *   APPLICATION: Bank details
 *   OTHER: Communication
 *   (Financing, Applicants, Expenses always visible; Product/Affordability when HPP permissioned)
 *
 * DIP_ACCEPTED (and later mapped pre-fees statuses)
 *   APPLICATION: Property, Solicitors
 *
 * FEES_PAID / REVIEWING_APPLICATION / OFFER_ISSUED / OFFER_EXPIRED
 *   same as DIP_ACCEPTED + Valuation (when valuation data exists)
 *
 * OFFER_ACCEPTED / APPLICATION_DECLINED / COMPLETION
 *   same as FEES_PAID + Post Offer (when conveyancing data exists)
 *
 * Overview is always shown separately (like BTL Case Summary).
 * Financing, Applicants and Expenses are always visible in APPLICATION.
 */

export const OO_STATUS_CHAIN: ApplicationStatus[] = [
  ApplicationStatus.APPLICATION_SUBMITTED,
  ApplicationStatus.DIP_ACCEPTED,
  ApplicationStatus.FEES_PAID,
  ApplicationStatus.OFFER_ACCEPTED,
];

export const OO_STATUS_SIDEBAR_CHANGES: Partial<Record<ApplicationStatus, StatusSidebarChange>> = {
  [ApplicationStatus.APPLICATION_SUBMITTED]: {
    add: {
      [CaseSidebarSection.DOCUMENTS]: [CaseSidebarItemId.CUSTOMER_FILES, CaseSidebarItemId.EVIDENCE],
      [CaseSidebarSection.APPLICATION]: [CaseSidebarItemId.BANK_DETAILS],
      [CaseSidebarSection.OTHER]: [CaseSidebarItemId.COMMUNICATION],
    },
  },
  [ApplicationStatus.DIP_ACCEPTED]: {
    add: {
      [CaseSidebarSection.APPLICATION]: [CaseSidebarItemId.PROPERTY, CaseSidebarItemId.SOLICITORS],
    },
  },
  [ApplicationStatus.FEES_PAID]: {
    add: {
      [CaseSidebarSection.APPLICATION]: [CaseSidebarItemId.VALUATION],
    },
  },
  [ApplicationStatus.OFFER_ACCEPTED]: {
    add: {
      [CaseSidebarSection.APPLICATION]: [CaseSidebarItemId.POST_OFFER],
    },
  },
};

export const OO_STATUS_TO_CHAIN_STATUS: Partial<Record<ApplicationStatus, ApplicationStatus>> = {
  [ApplicationStatus.DIP_SUBMITTED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.REVIEWING_DIP]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.DIP_REFERRED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.DIP_DECLINED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.DIP_EXPIRED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.BTL_APPLICATION_SUBMITTED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.RECOMMENDED]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.PAYMENT_DUE]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.PROPERTY_DECLINED]: ApplicationStatus.APPLICATION_SUBMITTED,
  [ApplicationStatus.DIP_ACCEPTED]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.AWAITING_PROPERTY]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.PROPERTY_SUBMITTED]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.BOOKING_PRE_ADVICE]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.PRE_ADVICE_BOOKED]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.ADVICE_BOOKED]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.PRODUCT_NOT_SUITABLE]: ApplicationStatus.DIP_ACCEPTED,
  [ApplicationStatus.REVIEWING_APPLICATION]: ApplicationStatus.FEES_PAID,
  [ApplicationStatus.OFFER_ISSUED]: ApplicationStatus.FEES_PAID,
  [ApplicationStatus.OFFER_EXPIRED]: ApplicationStatus.FEES_PAID,
  [ApplicationStatus.OFFER_BEING_REVISED]: ApplicationStatus.FEES_PAID,
  [ApplicationStatus.APPLICATION_DECLINED]: ApplicationStatus.OFFER_ACCEPTED,
  [ApplicationStatus.COMPLETION]: ApplicationStatus.OFFER_ACCEPTED,
};

export function isAfterOoDipAccepted(status?: string): boolean {
  if (!status) {
    return false;
  }

  const chainStatus = OO_STATUS_TO_CHAIN_STATUS[status as ApplicationStatus] ?? (status as ApplicationStatus);
  const dipAcceptedChainIndex = OO_STATUS_CHAIN.indexOf(ApplicationStatus.DIP_ACCEPTED);
  const currentChainIndex = OO_STATUS_CHAIN.indexOf(chainStatus);

  if (currentChainIndex === -1 || dipAcceptedChainIndex === -1) {
    return false;
  }

  return currentChainIndex > dipAcceptedChainIndex;
}

/** OO statuses after DIP accepted (excluding exact DIP_ACCEPTED) until fees are paid. */
export const OO_POST_DIP_PRE_FEES_STATUSES = [
  ApplicationStatus.DIP_ACCEPTED,
  ApplicationStatus.AWAITING_PROPERTY,
  ApplicationStatus.PROPERTY_SUBMITTED,
  ApplicationStatus.BOOKING_PRE_ADVICE,
  ApplicationStatus.PRE_ADVICE_BOOKED,
  ApplicationStatus.ADVICE_BOOKED,
  ApplicationStatus.RECOMMENDED,
  ApplicationStatus.PRODUCT_NOT_SUITABLE,
] as const;

export function isOoPostDipPreFeesStatus(status?: string): boolean {
  if (!status) {
    return false;
  }

  return (OO_POST_DIP_PRE_FEES_STATUSES as readonly string[]).includes(status);
}

export function isOoProductEditableStatus(status?: string, canEditDipAcceptedSections?: boolean): boolean {
  if (!status) {
    return true;
  }

  if (status === ApplicationStatus.DIP_ACCEPTED) {
    return Boolean(canEditDipAcceptedSections);
  }

  if (isOoPostDipPreFeesStatus(status)) {
    return false;
  }

  return !isAfterOoDipAccepted(status);
}
